import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { api, apiJSON } from "../../lib/api";

type Position = {
  title: string;
  standard?: string;
  fallback?: string;
  rationale?: string;
  citation?: string;
  walkAwayMin?: string;
};

type Playbook = {
  id: string;
  kind: string;
  side: string;
  name: string;
  description: string;
  positions: Position[];
  dealBreakers: Position[];
};

const KINDS = ["CONTRACT_REVIEW", "CONTRACT_DRAFT", "DEMANDA", "RESPONSE", "OTHER"];
const SIDES = ["BUYER", "SELLER", "LANDLORD", "TENANT", "PLAINTIFF", "DEFENDANT", "NEUTRAL"];

function emptyPosition(): Position {
  return { title: "", standard: "", fallback: "", rationale: "", citation: "", walkAwayMin: "" };
}

function PositionEditor({
  label,
  rows,
  onChange,
}: {
  label: string;
  rows: Position[];
  onChange: (rows: Position[]) => void;
}) {
  const update = (idx: number, key: keyof Position, value: string) => {
    const copy = rows.slice();
    copy[idx] = { ...copy[idx], [key]: value };
    onChange(copy);
  };
  return (
    <fieldset className="mt-4 rounded-md border border-slate-200 p-3">
      <legend className="px-2 text-sm font-medium text-brand-900">{label}</legend>
      {rows.map((p, idx) => (
        <div key={idx} className="mt-3 grid gap-2 rounded bg-slate-50 p-2 md:grid-cols-2">
          <input
            placeholder="Title"
            value={p.title}
            onChange={(e) => update(idx, "title", e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm md:col-span-2"
          />
          <input
            placeholder="Standard position"
            value={p.standard ?? ""}
            onChange={(e) => update(idx, "standard", e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm md:col-span-2"
          />
          <input
            placeholder="Acceptable fallback"
            value={p.fallback ?? ""}
            onChange={(e) => update(idx, "fallback", e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <input
            placeholder="Walk-away (bottom of range)"
            value={p.walkAwayMin ?? ""}
            onChange={(e) => update(idx, "walkAwayMin", e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <input
            placeholder="Rationale"
            value={p.rationale ?? ""}
            onChange={(e) => update(idx, "rationale", e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <input
            placeholder="Citation (statute/case)"
            value={p.citation ?? ""}
            onChange={(e) => update(idx, "citation", e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <div className="md:col-span-2 text-right">
            <button
              type="button"
              onClick={() => onChange(rows.filter((_, i) => i !== idx))}
              className="text-xs text-rose-700 hover:underline"
            >
              Remove
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, emptyPosition()])}
        className="mt-3 rounded-md border border-dashed border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
      >
        + Add {label.toLowerCase()}
      </button>
    </fieldset>
  );
}

function Editor({ value, onSave, onClose }: { value: Playbook; onSave: (pb: Playbook) => Promise<void>; onClose: () => void }) {
  const [pb, setPb] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(pb);
      onClose();
    } catch (err) {
      setError(String((err as Error).message ?? err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-6 rounded-lg border border-slate-300 bg-white p-4">
      <div className="grid gap-3 md:grid-cols-3">
        <input
          required
          placeholder="Name"
          value={pb.name}
          onChange={(e) => setPb({ ...pb, name: e.target.value })}
          className="rounded border border-slate-300 px-3 py-2 text-sm md:col-span-3"
        />
        <select
          value={pb.kind}
          onChange={(e) => setPb({ ...pb, kind: e.target.value })}
          className="rounded border border-slate-300 px-3 py-2 text-sm"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select
          value={pb.side}
          onChange={(e) => setPb({ ...pb, side: e.target.value })}
          className="rounded border border-slate-300 px-3 py-2 text-sm"
        >
          {SIDES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <textarea
        placeholder="Description (what the LLM should know about this playbook)"
        value={pb.description}
        rows={3}
        onChange={(e) => setPb({ ...pb, description: e.target.value })}
        className="mt-3 w-full rounded border border-slate-300 px-3 py-2 text-sm"
      />
      <PositionEditor label="Positions" rows={pb.positions} onChange={(rows) => setPb({ ...pb, positions: rows })} />
      <PositionEditor label="Deal breakers" rows={pb.dealBreakers} onChange={(rows) => setPb({ ...pb, dealBreakers: rows })} />
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
        >
          {saving ? "…" : "Save"}
        </button>
        <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function PlaybooksPage() {
  const { session } = useAuth();
  const [list, setList] = useState<Playbook[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Playbook | null>(null);

  const refresh = () => {
    if (!session) return;
    apiJSON<{ playbooks: Playbook[] }>("/api/v1/tenant/playbooks", { token: session.token })
      .then((r) => setList(r.playbooks))
      .catch((e) => setError(String(e.message ?? e)));
  };
  useEffect(refresh, [session]);

  const save = async (pb: Playbook) => {
    const isNew = !pb.id;
    const path = isNew
      ? "/api/v1/tenant/playbooks"
      : `/api/v1/tenant/playbooks/${pb.id}`;
    const res = await api(path, {
      method: isNew ? "POST" : "PUT",
      token: session?.token,
      body: JSON.stringify(pb),
    });
    if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
    refresh();
  };

  const remove = async (id: string) => {
    await api(`/api/v1/tenant/playbooks/${id}`, { method: "DELETE", token: session?.token });
    refresh();
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">Playbooks</h1>
      <p className="mt-2 text-sm text-slate-500">
        Side-specific negotiation positions + deal breakers. Pass a playbook id on draft
        creation and Claude grades the document against it.
      </p>
      <button
        type="button"
        onClick={() =>
          setEditing({
            id: "",
            kind: "CONTRACT_REVIEW",
            side: "NEUTRAL",
            name: "",
            description: "",
            positions: [],
            dealBreakers: [],
          })
        }
        className="mt-4 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500"
      >
        + New playbook
      </button>
      {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
      {editing && <Editor value={editing} onSave={save} onClose={() => setEditing(null)} />}
      {list && list.length === 0 && <p className="mt-6 text-slate-500">No playbooks yet.</p>}
      <ul className="mt-6 space-y-3">
        {(list ?? []).map((p) => (
          <li key={p.id} className="rounded-md bg-white p-3 ring-1 ring-slate-200">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{p.name}</span>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{p.kind}</span>
                  <span className="text-xs text-slate-500">side: {p.side}</span>
                </div>
                {p.description && <p className="mt-1 text-xs text-slate-500">{p.description}</p>}
                <p className="mt-1 text-xs text-slate-400">
                  {p.positions.length} positions · {p.dealBreakers.length} deal breakers
                </p>
              </div>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={() => setEditing(p)} className="text-brand-700 hover:underline">
                  Edit
                </button>
                <button type="button" onClick={() => remove(p.id)} className="text-rose-700 hover:underline">
                  Delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
